import { useState } from "react";
import { Form, Input, Button, Typography, message } from "antd";
import {
  UserOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { useNavigate, Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/services/api";
import { useAuthStore } from "@/stores/authStore";
import type { UserRoleValue } from "@/types";

// Set VITE_LANDING_URL=http://localhost:5556 in admin/.env.local only if running
// the landing page as a separate dev server. For prod (same domain), leave unset.
const LANDING_URL = import.meta.env.VITE_LANDING_URL ?? "/";

interface LoginForm {
  email: string;
  password: string;
}

interface LoginResponse {
  data:
    | { requiresOtp: true; userId: string }
    | {
        user: {
          id: string;
          name: string;
          email: string;
          role: string;
          teacherStatus?: string | null;
        };
        accessToken: string;
        refreshToken: string;
      };
}

interface OtpResponse {
  data: {
    user: { id: string; name: string; email: string; role: string };
    accessToken: string;
    refreshToken: string;
  };
}

const CardWiseLogo = () => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 64,
      height: 64,
      borderRadius: 18,
      background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
      marginBottom: 16,
      boxShadow: "0 4px 20px rgba(99,102,241,0.35)",
    }}
  >
    <span style={{ color: "white", fontWeight: 800, fontSize: 28 }}>M</span>
  </div>
);

export default function LoginPage() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [otpStep, setOtpStep] = useState<
    { active: false } | { active: true; userId: string }
  >({ active: false });

  const loginMutation = useMutation({
    mutationFn: (data: LoginForm) =>
      api.post<unknown, LoginResponse>("/auth/login", data),
    onSuccess: (res) => {
      const payload = res.data;

      if ("requiresOtp" in payload && payload.requiresOtp) {
        setOtpStep({ active: true, userId: payload.userId });
        return;
      }

      if ("user" in payload) {
        const { user, accessToken, refreshToken } = payload;
        setAuth(
          {
            id: user.id,
            name: user.name,
            email: user.email,
            role: user.role as UserRoleValue,
            teacherStatus: user.teacherStatus,
          },
          accessToken,
          refreshToken,
        );
        redirectByRole(user.role);
      }
    },
    onError: (err: unknown) => {
      const code = (err as { error?: { code?: string } })?.error?.code;
      if (code === "ACCOUNT_INACTIVE") {
        message.error("Your account is inactive. Please contact support.", 5);
      } else if (code === "ACCOUNT_LOCKED") {
        message.error(
          "Too many failed attempts. Account locked for 15 minutes.",
          6,
        );
      } else if (code === "EMAIL_NOT_VERIFIED") {
        message.error("Please verify your email before logging in.", 5);
      } else {
        message.error("Invalid email or password");
      }
    },
  });

  const otpMutation = useMutation({
    mutationFn: ({ userId, otp }: { userId: string; otp: string }) =>
      api.post<unknown, OtpResponse>("/auth/admin/verify-otp", { userId, otp }),
    onSuccess: (res) => {
      const { user, accessToken, refreshToken } = res.data;
      setAuth(
        {
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role as UserRoleValue,
        },
        accessToken,
        refreshToken,
      );
      redirectByRole(user.role);
    },
    onError: () => message.error("Invalid OTP. Please try again."),
  });

  function redirectByRole(role: string) {
    switch (role) {
      case "SUPER_ADMIN":
      case "ADMIN":
        navigate("/admin");
        break;
      case "TEACHER":
        navigate("/teacher");
        break;
      case "STUDENT":
        navigate("/student");
        break;
      default:
        message.error("Invalid user role");
    }
  }

  const outerStyle: React.CSSProperties = {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #f8fafc 0%, #ede9fe 100%)",
    position: "relative",
  };

  const cardStyle: React.CSSProperties = {
    background: "#fff",
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    padding: "32px 36px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
  };

  // ── OTP step ──────────────────────────────────────────────────────────────
  if (otpStep.active) {
    return (
      <div style={outerStyle}>
        <div style={{ width: 420 }}>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 64,
                height: 64,
                borderRadius: 18,
                background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                marginBottom: 16,
                boxShadow: "0 4px 20px rgba(99,102,241,0.35)",
              }}
            >
              <SafetyCertificateOutlined
                style={{ color: "#fff", fontSize: 28 }}
              />
            </div>
            <Typography.Title level={3} style={{ margin: 0, color: "#0f172a" }}>
              Admin Verification
            </Typography.Title>
            <Typography.Text style={{ color: "#64748b", fontSize: 14 }}>
              Enter your 6-digit OTP to continue
            </Typography.Text>
          </div>

          <div style={cardStyle}>
            <Form
              onFinish={({ otp }: { otp: string }) =>
                otpMutation.mutate({ userId: otpStep.userId, otp })
              }
              layout="vertical"
              requiredMark={false}
            >
              <Form.Item
                name="otp"
                label={
                  <span style={{ fontWeight: 600, color: "#374151" }}>
                    OTP Code
                  </span>
                }
                rules={[
                  { required: true, message: "OTP is required" },
                  { len: 6, message: "OTP must be exactly 6 digits" },
                ]}
              >
                <Input
                  prefix={
                    <SafetyCertificateOutlined style={{ color: "#94a3b8" }} />
                  }
                  placeholder="Enter 6-digit OTP"
                  size="large"
                  maxLength={6}
                  style={{
                    letterSpacing: "0.3em",
                    fontWeight: 700,
                    fontSize: 18,
                  }}
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                loading={otpMutation.isPending}
                style={{ fontWeight: 700, height: 48, borderRadius: 10 }}
              >
                Verify &amp; Sign In
              </Button>
            </Form>

            <div style={{ textAlign: "center", marginTop: 20 }}>
              <Button
                type="link"
                onClick={() => setOtpStep({ active: false })}
                style={{ color: "#64748b", padding: 0 }}
              >
                ← Back to login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Login step ────────────────────────────────────────────────────────────
  return (
    <div style={outerStyle}>
      <div style={{ position: "absolute", top: 20, left: 24 }}>
        <Button
          type="text"
          onClick={() => {
            window.location.href = LANDING_URL;
          }}
          style={{ color: "#64748b", fontWeight: 600 }}
        >
          ← Back to home
        </Button>
      </div>

      <div style={{ width: 420 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <CardWiseLogo />
          <Typography.Title level={3} style={{ margin: 0, color: "#0f172a" }}>
            Welcome to CardWise
          </Typography.Title>
          <Typography.Text style={{ color: "#64748b", fontSize: 14 }}>
            Sign in to your account
          </Typography.Text>
        </div>

        <div style={cardStyle}>
          <Form
            onFinish={(values: LoginForm) =>
              loginMutation.mutate({ ...values, email: values.email.trim() })
            }
            layout="vertical"
            requiredMark={false}
          >
            <Form.Item
              name="email"
              label={
                <span style={{ fontWeight: 600, color: "#374151" }}>Email</span>
              }
              normalize={(v: string) => v.trim()}
              rules={[
                {
                  required: true,
                  type: "email",
                  message: "Enter a valid email",
                },
              ]}
            >
              <Input
                prefix={<UserOutlined style={{ color: "#94a3b8" }} />}
                placeholder="your@email.com"
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={
                <span style={{ fontWeight: 600, color: "#374151" }}>
                  Password
                </span>
              }
              rules={[{ required: true, message: "Password is required" }]}
              style={{ marginBottom: 24 }}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: "#94a3b8" }} />}
                placeholder="Enter your password"
                size="large"
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={loginMutation.isPending}
              style={{ fontWeight: 700, height: 48, borderRadius: 10 }}
            >
              Sign In
            </Button>
          </Form>

          <div style={{ textAlign: "center", marginTop: 20 }}>
            <Typography.Text style={{ color: "#64748b" }}>
              Don&apos;t have an account?{" "}
              <Link to="/register" style={{ fontWeight: 600 }}>
                Sign up
              </Link>
            </Typography.Text>
          </div>
        </div>
      </div>
    </div>
  );
}
